import { toDateFromServer } from '../api/dates';
import { Meal } from '../types';

/**
 * Check if a meal is available for a specific date
 * @param meal The meal to check
 * @param date The date to check (format: YYYY_MM_DD)
 * @returns true if the meal is available, false otherwise
 */
export function isMealAvailableForDate(meal: Meal, date: string): boolean {
    // If no ranges are set, the meal is always available
    if (!meal.Ranges || Object.keys(meal.Ranges).length === 0) {
        return true;
    }

    // Check if the date falls within any of the defined ranges
    const targetDate = toDateFromServer(date);

    for (const range of Object.values(meal.Ranges)) {
        const startDate = toDateFromServer(range.start);
        const endDate = toDateFromServer(range.end);

        if (targetDate >= startDate && targetDate <= endDate) {
            return true;
        }
    }

    return false;
}

/**
 * Validate that all meals in an order are available for the given date
 * @param meals The meals to validate
 * @param date The date to check availability for (format: YYYY_MM_DD)
 * @returns Array of meal keys that are not available
 */
export function validateMealAvailability(meals: { [key: string]: Meal }, date: string): string[] {
    const unavailableMeals: string[] = [];

    for (const [mealKey, meal] of Object.entries(meals)) {
        if (!isMealAvailableForDate(meal, date)) {
            unavailableMeals.push(mealKey);
        }
    }

    return unavailableMeals;
}

/**
 * Filter meals based on availability for a specific date
 * @param meals Array of meal snapshots
 * @param date The date to check availability for (format: YYYY_MM_DD)
 * @returns Array of meals that are available for the given date
 */
export function filterMealsByDateAvailability<T extends { val(): Meal }>(
    meals: T[], 
    date: string
): T[] {
    return meals.filter(mealSnapshot => {
        const meal = mealSnapshot.val();
        return isMealAvailableForDate(meal, date);
    });
}